import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { Button } from '@components/button';
import { CopyButton } from '@components/copy-button';
import { EmptyState } from '@components/empty-state';
import { createForm, useCreateFormStore } from '@components/form';
import { FormError } from '@components/form-error';
import { useDeleteModal } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconCopyContent } from '@components/icons/copy-content';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconCode } from '@components/icons/icon-code';
import { IconInput } from '@components/icons/input';
import { IconTrash } from '@components/icons/trash';
import { IconWebhook } from '@components/icons/webhook';
import { InfoBox } from '@components/info-box';
import { Spinner } from '@components/loading-spinner';
import { forceHideSidebar, InfoButton, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import {
  useDeleteJourneyWebhook,
  useJourneyWebhook,
  useJourneyWebhookLogs,
  useUpdateJourneyWebhook,
} from 'api/journey-webhooks/hooks';
import { JourneyWebhook } from 'api/journey-webhooks/requests';
import { useJourneyAttributes } from 'api/journeys/hooks';
import { Condition, ConditionType } from 'api/types';
import { FieldMappedAttributeField } from 'ui/actions/common';
import { getFieldMappingId } from 'ui/actions/helpers';
import {
  ActionHeader,
  DescriptionFormField,
  useRawVariableGroups,
} from 'ui/journey-builder/node-types/common';
import {
  FormWithUnsavedChangesPrompt,
  UnsavedChangesPrompter,
} from 'ui/journey-builder/unsaved-changes-prompt';

import { Conditions, processConditionsForSubmission, useConditionState } from './condition-helpers';

import { TriggerLogListCard } from '../common';
import { usePreviewWebhookLog } from '../modals/preview-webhook-log';
import { useWebhookAutoConfig } from '../modals/webhook-configure-automatically';
import { useWebhookSampleData } from '../modals/webhook-sample-data';

type Payload = {
  description: string;
  isActive: boolean;
  isAutoStart: boolean;
  [key: string]: any;
};

type Data = Payload & {
  name: string;
  email: string;
  phone: string;
  conditionType: ConditionType;
  conditions: Condition[];
};

const Form = createForm<Data>();

export const useWebhookSidebar = () => {
  const sidebar = useSidebar(WebhookSidebar, {
    variant: 'REGULAR',
  });
  return sidebar;
};

type WebhookProps = {
  webhookShortId: string;
};

export const WebhookSidebar = ({ webhookShortId }: WebhookProps) => {
  const { t } = useScopedTranslate(locale);
  const { data: webhook, isLoading } = useJourneyWebhook(webhookShortId);

  if (!webhook || isLoading) {
    return <Spinner />;
  }

  const tabItems = [
    {
      label: t('details'),
      content: <WebhookContent webhook={webhook} />,
    },
    {
      label: t('logs'),
      content: <WebhookLogs webhookShortId={webhookShortId} />,
      tabCount: webhook.logsCount,
    },
  ];
  return (
    <Sidebar>
      <ActionHeader
        icon={IconWebhook}
        iconColor="elementOrange100"
        title={t('webhook')}
        tabs={tabItems}
      >
        <InfoButton>
          <Info />
        </InfoButton>
      </ActionHeader>
    </Sidebar>
  );
};

const Info = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={4}>
      <Text font="bold16" text={t('webhookTrigger')} />
      <Text font="regular14">
        {styledTranslate({
          key: 'helpText',
          variables: {
            clickHereToReadMore: {
              font: 'regular14',
              text: (
                <a
                  href="https://help.journeys.50skills.app/en/articles/382530-webhook-triggers"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                >
                  {t('clickHereToReadMore')}
                </a>
              ),
            },
          },
          translations: locale,
        })}
      </Text>
    </Stack>
  );
};

const WebhookContent = ({ webhook }: { webhook: JourneyWebhook }) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const formValues = formStore.useFormValues();
  const isDirty = formStore.useIsDirty();
  const [notConfiguredError, setNotConfiguredError] = useState(false);
  const showWebhookSampleDataModal = useWebhookSampleData();
  const showWebhookAutoConfigModal = useWebhookAutoConfig();
  const updateMutation = useUpdateJourneyWebhook(webhook);
  const [fields, setFields] = useState<string[]>([
    'company_traveller_name',
    'traveller_email',
    'company_traveller_phone',
  ]);

  const initialConditions = useMemo(
    () => webhook?.conditions?.conditions ?? [],
    [webhook?.conditions?.conditions]
  );

  const {
    conditions,
    setConditions,
    conditionsChanged,
    handleAddCondition,
    handleRemoveCondition,
  } = useConditionState(initialConditions);

  const handleSubmit = async (data: Data) => {
    const mappings = Object.fromEntries(fields.map((attrId) => [attrId, data[attrId]]));
    const hasAnyMapping = Object.values(mappings).some((value) => value !== undefined);
    if (!hasAnyMapping) {
      setNotConfiguredError(true);
      return;
    }

    const formConditions = processConditionsForSubmission(conditions, formValues);

    const payload: Payload = {
      description: data.description,
      isActive: data.isActive,
      isAutoStart: data.isAutoStart,
      mappings,
      conditions: {
        conditions: formConditions,
        conditionType: data.conditionType,
      },
    };

    await updateMutation.mutateAsync(payload, { onSuccess: forceHideSidebar });
  };

  const hasMappings = Object.values(webhook.mappings).some((value) => value !== undefined);

  const hasConfigured = [
    webhook.payloadVariables,
    webhook.headerVariables,
    webhook.fileVariables,
  ].some((vars) => vars.length > 0);

  const fieldMappingId = getFieldMappingId(webhook.journeyShortId);

  const baseVariableGroups = useRawVariableGroups({
    actionVariables: [
      {
        id: 'payload',
        label: t('payload'),
        fields: webhook.payloadVariables.map((variable) => ({
          label: variable.label,
          field: variable.name,
          type: variable.type,
          source: 'payload',
        })),
      },
      {
        id: 'header',
        label: t('headers'),
        fields: webhook.headerVariables.map((variable) => ({
          label: variable.label,
          field: variable.name,
          type: variable.type,
          source: 'headers',
        })),
      },
      {
        id: 'files',
        label: t('files'),
        fields: webhook.fileVariables.map((variable) => ({
          label: variable.label,
          field: variable.name,
          type: variable.type,
          source: 'files',
        })),
      },
    ],
    types: [
      'email',
      'text',
      'paragraph',
      'phone',
      'date',
      'separator',
      'boolean',
      'file',
      'select',
      'multi_select',
      'number',
      'decimal',
    ],
  });

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSubmit}>
      <UnsavedChangesPrompter
        onSubmit={handleSubmit}
        overwriteIsDirty={hasConfigured && !hasMappings}
      />
      <Stack space={6}>
        <HideContainer title={t('triggerDetails')}>
          <Stack space={4}>
            <DescriptionFormField defaultValue={webhook.description} />
            <Stack orientation="horizontal" align="flex-end" space={2}>
              <div style={{ flex: 1 }}>
                <TextField
                  label={t('journeyUrl')}
                  defaultValue={webhook.triggerUrl}
                  isDisabled
                  readOnly
                />
              </div>
              <CopyButton getText={() => webhook.triggerUrl} size="md" iconOnly />
            </Stack>
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        {!hasMappings && (
          <>
            <HideContainer title={t('payload')}>
              {hasConfigured ? (
                <InfoBox
                  text={t('payloadAdded')}
                  variant="green"
                  icon={<IconFormIndicatorSuccess />}
                />
              ) : (
                <Stack space={4}>
                  <InfoBox text={t('specifyFormat')} variant="indigo" closable />
                  <Stack orientation="horizontal" space={2}>
                    <Button
                      text={t('configureAutomatically')}
                      iconLeft={<IconInput />}
                      variant="secondary"
                      color="textSecondary"
                      onClick={() => showWebhookAutoConfigModal({ webhook })}
                    />
                    <Button
                      text={t('pasteExampleData')}
                      iconLeft={<IconCopyContent />}
                      variant="secondary"
                      color="textSecondary"
                      onClick={() => showWebhookSampleDataModal({ webhook })}
                    />
                  </Stack>
                  {notConfiguredError && <FormError text={t('notConfiguredError')} />}
                </Stack>
              )}
            </HideContainer>
            <Sidebar.Separator />
          </>
        )}
        <HideContainer title={t('addConditions')} caps>
          <Conditions
            conditions={conditions}
            conditionType={webhook.conditions?.conditionType}
            variableGroups={baseVariableGroups}
            fieldMappingId={fieldMappingId}
            onAddCondition={handleAddCondition}
            onRemoveCondition={handleRemoveCondition}
            setConditions={setConditions}
            Form={Form}
          />
        </HideContainer>
        {hasConfigured && (
          <>
            <Sidebar.Separator />
            <HideContainer title={t('fieldMapping')} overflowType="visible">
              <FieldMap webhook={webhook} setFields={setFields} />
            </HideContainer>
          </>
        )}
        <Sidebar.Separator />
        <HideContainer title={t('options')}>
          <Stack space={4}>
            <Form.Switch
              key={`isActive-${webhook.isActive}`}
              id="isActive"
              label={t('active')}
              defaultValue={webhook.isActive}
            />
            <Form.Switch
              key={`isAutoStart-${webhook.isAutoStart}`}
              id="isAutoStart"
              label={t('startOnSubmission')}
              defaultValue={webhook.isAutoStart}
            />
          </Stack>
        </HideContainer>
      </Stack>
      <Footer
        webhook={webhook}
        isConfigured={hasConfigured}
        isDisabled={!isDirty && !conditionsChanged}
      />
    </FormWithUnsavedChangesPrompt>
  );
};

const WebhookLogs = ({ webhookShortId }: WebhookProps) => {
  const { t } = useScopedTranslate(locale);
  const { data, isLoading } = useJourneyWebhookLogs(webhookShortId);
  const showPreviewWebhookLog = usePreviewWebhookLog();

  if (!data || isLoading) {
    return <Spinner />;
  }

  if (data.length === 0) {
    return (
      <EmptyState
        header={t('noLogs')}
        content={t('webhookHasNotBeenTriggered')}
        icon={<IconInput />}
        variant="small"
      />
    );
  }

  return (
    <Stack space={2}>
      {data.map((log) => {
        return (
          <TriggerLogListCard
            key={log.shortId}
            icon={<IconCode color="elementPurple80" />}
            title={abbreviateDate(log.triggeredOnDate, 'en')}
            description={log.triggeredOnTime}
            onClick={() =>
              showPreviewWebhookLog({ webhookShortId, webhookLogShortId: log.shortId })
            }
          />
        );
      })}
    </Stack>
  );
};

const FieldMap = ({
  webhook,
  setFields,
}: {
  webhook: JourneyWebhook;
  setFields: Dispatch<SetStateAction<string[]>>;
}) => {
  const { t } = useScopedTranslate(locale);
  const journeyAttributeTemplatesQuery = useJourneyAttributes(webhook.journeyShortId, {
    ignore_inactive: true,
  });
  const companyTravellerAttributeTemplatesQuery = useCompanyTravellerAttributeTemplates({
    queryData: { ignore_inactive: true },
  });

  const actionVariables = useMemo(
    () => [
      {
        id: 'payload',
        label: t('payload'),
        fields: webhook.payloadVariables.map((variable) => ({
          label: variable.label,
          field: variable.name,
          type: variable.type,
          source: 'payload',
        })),
      },
      {
        id: 'header',
        label: t('headers'),
        fields: webhook.headerVariables.map((variable) => ({
          label: variable.label,
          field: variable.name,
          type: variable.type,
          source: 'headers',
        })),
      },
      {
        id: 'files',
        label: t('files'),
        fields: webhook.fileVariables.map((variable) => ({
          label: variable.label,
          field: variable.name,
          type: variable.type,
          source: 'files',
        })),
      },
    ],
    [t, webhook.headerVariables, webhook.payloadVariables, webhook.fileVariables]
  );

  const variableGroups = useRawVariableGroups({
    actionVariables,
    types: ['text'],
  });

  const emailVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['email'],
  });

  const phoneVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['phone'],
  });

  const dateVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['date'],
  });

  const fileVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['file', 'text'],
  });

  const fileMultiVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['file', 'file_multi', 'text'],
  });

  const optionsVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['select', 'text'],
  });

  const numberVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['number'],
  });

  const decimalVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['number', 'decimal'],
  });

  const journeyAttributes = useMemo(
    () => journeyAttributeTemplatesQuery.data ?? [],
    [journeyAttributeTemplatesQuery.data]
  );

  const travellerAttributes = useMemo(
    () => companyTravellerAttributeTemplatesQuery.data ?? [],
    [companyTravellerAttributeTemplatesQuery.data]
  );

  useEffect(() => {
    setFields((currentFields) => [
      ...currentFields,
      ...journeyAttributes.map((f) => f.shortId),
      ...travellerAttributes.map((f) => f.id),
    ]);
  }, [journeyAttributes, travellerAttributes, setFields]);

  if (
    journeyAttributeTemplatesQuery.isLoading ||
    !journeyAttributeTemplatesQuery.data ||
    companyTravellerAttributeTemplatesQuery.isLoading ||
    !companyTravellerAttributeTemplatesQuery.data
  ) {
    return <Spinner />;
  }
  const fieldMappingId = getFieldMappingId(webhook.journeyShortId);

  return (
    <Stack space={4}>
      <HideContainer
        title={t('travellerFields')}
        icon={<IconAttributeTraveller color="buttonIconPositive" />}
      >
        <Stack space={4}>
          <Form.FieldMappingTextEditor
            id="company_traveller_name"
            label={t('core.fullName')}
            validators={['required']}
            defaultValue={webhook.mappings['companyTravellerName']}
            variableGroups={variableGroups}
            fieldMappingId={fieldMappingId}
            singleLine
            plaintext
          />
          <Form.EmailField
            id="traveller_email"
            label={t('core.email')}
            validators={['required']}
            defaultValue={webhook.mappings['travellerEmail']}
            variableGroups={emailVariableGroups}
            fieldMappingId={fieldMappingId}
          />
          <Form.FieldMappingPhoneField
            id="company_traveller_phone"
            label={t('core.phone')}
            defaultValue={webhook.mappings['companyTravellerPhone']}
            variableGroups={phoneVariableGroups}
            fieldMappingId={fieldMappingId}
          />

          {travellerAttributes.map((attribute) => {
            const defaultField = webhook.mappings[attribute.shortId];
            const isUserAttribute = attribute.attributeType === 'user';
            if (isUserAttribute) {
              return null;
            }
            return (
              <FieldMappedAttributeField
                key={attribute.id}
                overwriteValueId={attribute.id}
                field={{
                  id: attribute.id,
                  value: defaultField,
                }}
                attribute={attribute}
                form={Form}
                variableGroups={variableGroups}
                emailVariableGroups={emailVariableGroups}
                phoneVariableGroups={phoneVariableGroups}
                dateVariableGroups={dateVariableGroups}
                optionsVariableGroups={optionsVariableGroups}
                fileVariableGroups={fileVariableGroups}
                fileMultiVariableGroups={fileMultiVariableGroups}
                numberVariableGroups={numberVariableGroups}
                decimalVariableGroups={decimalVariableGroups}
                journeyId={webhook.journeyShortId}
                filePath=""
              />
            );
          })}
        </Stack>
      </HideContainer>
      {journeyAttributes.length > 0 && (
        <>
          <Sidebar.Separator />
          <HideContainer
            title={t('journeyFields')}
            icon={<IconAttributeJourney color="buttonIconPositive" />}
          >
            <Stack space={4}>
              {journeyAttributes.map((attribute) => {
                const defaultField = webhook.mappings[attribute.shortId];
                const isUserAttribute = attribute.attributeType === 'user';
                if (isUserAttribute) {
                  return null;
                }
                return (
                  <FieldMappedAttributeField
                    key={attribute.shortId}
                    overwriteValueId={attribute.shortId}
                    field={{
                      id: attribute.shortId,
                      value: defaultField,
                    }}
                    attribute={attribute}
                    form={Form}
                    variableGroups={variableGroups}
                    emailVariableGroups={emailVariableGroups}
                    phoneVariableGroups={phoneVariableGroups}
                    dateVariableGroups={dateVariableGroups}
                    optionsVariableGroups={optionsVariableGroups}
                    fileVariableGroups={fileVariableGroups}
                    fileMultiVariableGroups={fileMultiVariableGroups}
                    numberVariableGroups={numberVariableGroups}
                    decimalVariableGroups={decimalVariableGroups}
                    filePath=""
                    journeyId={webhook.journeyShortId}
                  />
                );
              })}
            </Stack>
          </HideContainer>
        </>
      )}
    </Stack>
  );
};

type FooterProps = {
  webhook: JourneyWebhook;
  isConfigured: boolean;
  isDisabled: boolean;
};

const Footer = ({ webhook, isConfigured, isDisabled }: FooterProps) => {
  const { t } = useScopedTranslate(locale);
  const deleteMutation = useDeleteJourneyWebhook(webhook.journeyShortId);
  const showDelete = useDeleteModal();

  const handleCancel = async () => {
    if (!isConfigured) {
      await deleteMutation.mutateAsync(webhook.shortId, { onSuccess: forceHideSidebar });
    } else {
      forceHideSidebar();
    }
  };

  return (
    <Sidebar.Footer>
      <Stack orientation="horizontal" justify="space-between" align="center">
        <IconButton
          variant="ghost-white"
          icon={<IconTrash color="purple50" />}
          onClick={() =>
            showDelete({
              title: t('removeTitle'),
              description: t('removeDescription'),
              buttonText: t('core.delete'),
              hasUserConfirmation: true,
              onConfirm: () =>
                deleteMutation.mutateAsync(webhook.shortId, { onSuccess: forceHideSidebar }),
            })
          }
        />
        <Stack orientation="horizontal" space={2}>
          <Button variant="secondary" text={t('core.cancel')} onClick={handleCancel} />
          <Form.Submit
            data-cy="action-save-btn"
            variant="primary-blue"
            text={t('core.save')}
            isDisabled={isDisabled || !isConfigured}
          />
        </Stack>
      </Stack>
    </Sidebar.Footer>
  );
};

export const locale = {
  en: {
    webhook: 'Webhook',
    details: 'Details',
    logs: 'Logs',
    triggerDetails: 'Trigger details',
    description: 'Description',
    journeyUrl: 'Journey URL',
    fieldMapping: 'Field mapping',
    payload: 'Payload',
    payloadAdded: 'Payload added',
    headers: 'Headers',
    files: 'Files',
    specifyFormat: 'Specify the format of the incoming payload to use the data in this Journey',
    configureAutomatically: 'Configure automatically',
    pasteExampleData: 'Paste example data',
    options: 'Options',
    copy: 'Copy',
    copied: 'Copied',
    active: 'Active',
    startOnSubmission: 'Start on submission',
    travellerFields: 'Traveller profile fields',
    journeyFields: 'Journey fields',
    notConfiguredError: 'Please configure your webhook before saving',
    removeTitle: 'Delete webhook',
    removeDescription: 'Are you sure you want to delete this webhook?',
    noLogs: 'No logs',
    webhookTrigger: 'Webhook trigger',
    webhookHasNotBeenTriggered: 'The webhook has never been triggered',
    helpText:
      'This trigger starts a workflow instantly when your endpoint receives data from another service. Instead of checking on a schedule, it reacts in real time as soon as something happens manually. \n\n{clickHereToReadMore}',
    clickHereToReadMore: 'Click here to read more',
    addConditions: 'Add conditions',
  },
};
